import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

// Kendo UI imports
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { IconsModule } from '@progress/kendo-angular-icons';
import { xIcon, SVGIcon } from '@progress/kendo-svg-icons';

// Core imports
import { BaseComponent } from 'src/app/core/shared/common/base.component';

import { IrContact, IncidentHandlingData } from 'src/app/core/models/customer';

@Component({
  selector: 'app-incident-handling',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputsModule, LabelModule, LayoutModule, IconsModule],
  templateUrl: './incident-handling.component.html',
  styleUrls: ['./incident-handling.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IncidentHandlingComponent extends BaseComponent implements OnInit {
  private fb = inject(FormBuilder);

  // Input/Output
  public formData = input<IncidentHandlingData>();
  public dataUpdate = output<IncidentHandlingData>();
  public validityUpdate = output<boolean>();

  // Form
  public incidentHandlingForm = this.fb.group({
    incidentHandlingEmail: ['', [Validators.required, Validators.email]],
    irContacts: this.fb.array([]),
  });

  // Icons
  public xIcon: SVGIcon = xIcon;

  // Computed properties
  public isFormValid = computed(() => this.incidentHandlingForm.valid);
  public irContacts = computed(() => this.getIrContacts());

  constructor() {
    super();
    this.initializeForm();
  }

  private initializeForm(): void {
    // Watch for form changes and emit updates
    this.incidentHandlingForm.valueChanges.subscribe((value) => {
      const cleanValue = this.buildCleanFormData(value);
      this.dataUpdate.emit(cleanValue);
      // Only check if incident handling email is valid, IR contacts are optional
      const isEmailValid = this.incidentHandlingForm.get('incidentHandlingEmail')?.valid;
      this.validityUpdate.emit(!!isEmailValid);
    });
  }

  ngOnInit(): void {
    // Initialize with existing data if provided
    const existingData = this.formData();
    if (existingData) {
      this.populateForm(existingData);
    }
  }

  private buildCleanFormData(value: any): IncidentHandlingData {
    return {
      incidentHandlingEmail: value.incidentHandlingEmail || '',
      irContacts: value.irContacts || [],
    };
  }

  private populateForm(data: IncidentHandlingData): void {
    // Set incident handling email
    this.incidentHandlingForm.patchValue({
      incidentHandlingEmail: data.incidentHandlingEmail || '',
    });

    // Clear existing IR contacts
    const irContactsArray = this.incidentHandlingForm.get('irContacts') as any;
    while (irContactsArray.length !== 0) {
      irContactsArray.removeAt(0);
    }

    // Add existing IR contacts
    if (data.irContacts && data.irContacts.length > 0) {
      data.irContacts.forEach((contact) => {
        this.addIrContact(contact);
      });
    }
  }

  public addIrContact(contact?: IrContact): void {
    const irContactsArray = this.incidentHandlingForm.get('irContacts') as any;
    if (irContactsArray) {
      const newContact = this.fb.group({
        name: [contact?.name || '', [Validators.required]],
        email: [contact?.email || '', [Validators.required, Validators.email]],
        phone: [contact?.phone || '', [Validators.required]],
      });
      irContactsArray.push(newContact);
    }
  }

  public removeIrContact(index: number): void {
    const irContactsArray = this.incidentHandlingForm.get('irContacts') as any;
    if (irContactsArray && irContactsArray.length > index) {
      irContactsArray.removeAt(index);
    }
  }

  private getIrContacts(): any[] {
    const irContactsArray = this.incidentHandlingForm.get('irContacts') as any;
    return irContactsArray?.controls || [];
  }
}
